import { useEffect, useState } from "react";
import { supabase } from "../lib/supabase.js";
import { formatAuPhoneForDisplay } from "../lib/phone.js";
import { Badge, Button, Modal } from "../lib/ui.js";
import { formatDateTime } from "../lib/datetime.js";
import { isUnresolvedEnquiry } from "../lib/questionableCalls.js";
import { useOptionalAuth, useReadOnly } from "../lib/auth.js";

interface ReviewFlag {
  id: string;
  message: string;
  booking_id: string | null;
}

interface CallbackNote {
  id: string;
  reason: string | null;
  preferred_time: string | null;
  status: "open" | "contacted" | "closed";
}

interface CallDetailRow {
  id: string;
  caller_number: string | null;
  started_at: string | null;
  ended_at: string | null;
  duration_seconds: number | null;
  outcome: string | null;
  transfer_state: string | null;
  headline: string | null;
  details: string | null;
  action_required: string | null;
  follow_up: string | null;
  reviewed_at: string | null;
}

/** Call details in a pop-up (from the Calls list and the dashboard). Reads v_call_detail: metadata +
 *  the curated call_summaries fields, joined server-side. Deliberately no "AI-generated" label
 *  anywhere here -- see docs section 7 point 2. */
export function CallDetailModal({ callId, onClose, onChanged }: { callId: string; onClose: () => void; onChanged?: () => void }) {
  const auth = useOptionalAuth();
  const readOnly = useReadOnly();
  // The rules page is owner-only, so only they get the link to it.
  const canTeach = !auth || auth.isPlatformAdmin || auth.memberships.find((m) => m.business_id === auth.activeBusinessId)?.role === "owner";
  const [row, setRow] = useState<CallDetailRow | null>(null);
  const [loading, setLoading] = useState(true);
  const [callbacks, setCallbacks] = useState<CallbackNote[]>([]);
  const [flags, setFlags] = useState<ReviewFlag[]>([]);
  const [flagBusy, setFlagBusy] = useState<string | null>(null);
  const [flagError, setFlagError] = useState<string | null>(null);
  const [flagsLoaded, setFlagsLoaded] = useState(false);
  const [callbacksLoaded, setCallbacksLoaded] = useState(false);
  const [reviewBusy, setReviewBusy] = useState(false);
  const [reviewError, setReviewError] = useState<string | null>(null);

  // Things on this call the system thinks staff should check (see the review rules in the voice service).
  async function loadFlags() {
    const { data } = await supabase
      .from("review_flags")
      .select("id, message, booking_id")
      .eq("call_id", callId)
      .eq("status", "open")
      .order("created_at");
    setFlags((data ?? []) as ReviewFlag[]);
    setFlagsLoaded(true);
  }
  useEffect(() => {
    void loadFlags();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [callId]);

  async function resolveFlag(flag: ReviewFlag, cancelBooking: boolean) {
    setFlagBusy(flag.id);
    setFlagError(null);
    const { error } = await supabase.rpc("rpc_portal_resolve_review_flag", { p_flag_id: flag.id, p_cancel_booking: cancelBooking });
    setFlagBusy(null);
    if (error) {
      setFlagError(error.message);
      return;
    }
    await loadFlags();
  }

  // "I have looked at this call": resolves its open flags (without cancelling anything) and records the call as reviewed, so the
  // dashboard card leaves it out. The same button for a flagged call and for an enquiry nothing came of.
  async function markReviewed() {
    setReviewBusy(true);
    setReviewError(null);
    const { error } = await supabase.rpc("rpc_portal_mark_call_reviewed", { p_call_id: callId });
    setReviewBusy(false);
    if (error) {
      setReviewError(error.message);
      return;
    }
    setFlags([]);
    setRow((r) => (r ? { ...r, reviewed_at: new Date().toISOString() } : r));
    onChanged?.();
  }

  // Callback requests logged during this call (RLS: business members read callback_requests).
  useEffect(() => {
    let cancelled = false;
    void supabase
      .from("callback_requests")
      .select("id, reason, preferred_time, status")
      .eq("call_id", callId)
      .order("created_at")
      .then(({ data }) => {
        if (cancelled) return;
        setCallbacks((data ?? []) as CallbackNote[]);
        setCallbacksLoaded(true);
      });
    return () => {
      cancelled = true;
    };
  }, [callId]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    void supabase
      .from("v_call_detail")
      .select("*")
      .eq("id", callId)
      .maybeSingle()
      .then(({ data }) => {
        if (cancelled) return;
        setRow(data as CallDetailRow | null);
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [callId]);

  const markReviewedButton = readOnly ? null : (
    <div style={{ marginTop: 4 }}>
      <Button variant="secondary" onClick={() => void markReviewed()} disabled={reviewBusy}>
        {reviewBusy ? "Saving..." : "Mark reviewed"}
      </Button>
      {reviewError && <p style={{ color: "var(--rc-danger)", fontSize: 12.5, margin: "8px 0 0" }}>{reviewError}</p>}
    </div>
  );

  return (
    <Modal title={row?.caller_number ? formatAuPhoneForDisplay(row.caller_number) : "Call details"} onClose={onClose} width={560}>
      {loading ? (
        <p style={{ margin: 0, color: "var(--rc-text-muted)" }}>Loading...</p>
      ) : !row ? (
        <p style={{ margin: 0, color: "var(--rc-text-muted)" }}>Call not found.</p>
      ) : (
        <>
          <dl style={{ fontSize: 14, lineHeight: 1.8, margin: 0 }}>
            <Row label="Started" value={row.started_at ? formatDateTime(row.started_at) : "-"} />
            <Row label="Ended" value={row.ended_at ? formatDateTime(row.ended_at) : "-"} />
            <Row label="Outcome" value={row.outcome ?? "-"} />
            <Row label="Transfer" value={row.transfer_state ?? "-"} />
          </dl>

          {flags.length > 0 && (
            <>
              <h3 style={{ fontSize: 15, marginTop: 20, marginBottom: 8 }}>Needs review</h3>
              {flags.map((flag) => (
                <div key={flag.id} style={{ background: "var(--rc-warning-soft)", border: "1px solid var(--rc-warning)", borderRadius: 8, padding: 16, fontSize: 14, marginBottom: 8 }}>
                  <p style={{ margin: "0 0 12px" }}>{flag.message}</p>
                  {flag.booking_id && !readOnly && (
                    <Button variant="danger" onClick={() => resolveFlag(flag, true)} disabled={flagBusy === flag.id}>
                      Cancel the old booking
                    </Button>
                  )}
                </div>
              ))}
              {flagError && <p style={{ color: "var(--rc-danger)", fontSize: 12.5, margin: "4px 0 0" }}>{flagError}</p>}
              {markReviewedButton}
            </>
          )}

          {!readOnly && flagsLoaded && callbacksLoaded && flags.length === 0 && callbacks.length === 0 && isUnresolvedEnquiry(row, false) && (
            <>
              <h3 style={{ fontSize: 15, marginTop: 20, marginBottom: 8 }}>No booking was made</h3>
              <div style={{ background: "var(--rc-surface-sunken)", border: "1px solid var(--rc-border)", borderRadius: 8, padding: 16, fontSize: 14, marginBottom: 8 }}>
                <p style={{ margin: "0 0 12px" }}>
                  The caller spoke with the receptionist, but nothing was booked, changed or left as a callback. Once you have looked into it, mark it reviewed to
                  clear it from your dashboard.
                </p>
                {markReviewedButton}
              </div>
            </>
          )}
          {row.reviewed_at && (
            <p style={{ margin: "16px 0 0", fontSize: 13, color: "var(--rc-text-muted)" }}>Marked reviewed on {formatDateTime(row.reviewed_at)}.</p>
          )}

          {callbacks.length > 0 && (
            <>
              <h3 style={{ fontSize: 15, marginTop: 20, marginBottom: 8 }}>Callback requested</h3>
              {callbacks.map((cb) => (
                <div key={cb.id} style={{ background: "var(--rc-surface-sunken)", border: "1px solid var(--rc-border)", borderRadius: 8, padding: 16, fontSize: 14, marginBottom: 8 }}>
                  <div style={{ marginBottom: 6 }}>
                    <Badge tone={cb.status === "open" ? "warning" : cb.status === "contacted" ? "primary" : "neutral"}>
                      {cb.status === "open" ? "Open" : cb.status === "contacted" ? "Contacted" : "Closed"}
                    </Badge>
                  </div>
                  <p style={{ margin: 0 }}>{cb.reason ?? "No details were recorded."}</p>
                  {cb.preferred_time && (
                    <p style={{ margin: "6px 0 0", color: "var(--rc-text-muted)" }}>Best time: {formatDateTime(cb.preferred_time)}</p>
                  )}
                </div>
              ))}
            </>
          )}

          <h3 style={{ fontSize: 15, marginTop: 20, marginBottom: 8 }}>Call summary</h3>
          {row.headline ? (
            <div style={{ background: "var(--rc-surface-sunken)", border: "1px solid var(--rc-border)", borderRadius: 8, padding: 16, fontSize: 14 }}>
              <p style={{ fontWeight: 600, marginTop: 0 }}>{row.headline}</p>
              {row.details && <p>{row.details}</p>}
              {row.action_required && (
                <p>
                  <strong>Action required:</strong> {row.action_required}
                </p>
              )}
              {row.follow_up && (
                <p style={{ marginBottom: 0 }}>
                  <strong>Follow-up:</strong> {row.follow_up}
                </p>
              )}
            </div>
          ) : (
            <p style={{ color: "var(--rc-text-faint)", margin: 0 }}>No summary for this call.</p>
          )}

          {canTeach && (
            <p style={{ margin: "16px 0 0", fontSize: 13 }}>
              Something it should have done differently?{" "}
              <a href={`/app/rules?call=${encodeURIComponent(callId)}`} style={{ color: "var(--rc-link)" }}>
                Teach the receptionist
              </a>
            </p>
          )}
        </>
      )}
    </Modal>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div style={{ display: "flex", gap: 8 }}>
      <dt style={{ width: 100, color: "var(--rc-text-muted)" }}>{label}</dt>
      <dd style={{ margin: 0 }}>{value}</dd>
    </div>
  );
}
